import { Injectable } from '@angular/core';
import { IInspectionOrderSteps } from 'src/app/interfaces/Outputs';
import {
  EInspectionStep,
  EInspectionStepUrl,
} from 'src/app/interfaces/PWA/Inspection';
import { BehaviorSubject } from 'rxjs';
import { Router } from '@angular/router';
import { IFeature } from 'src/app/interfaces/Outputs/Feature';
import { IAccesory } from 'src/app/interfaces/Outputs/Accesory';
import {
  FormSectionDataAndPhotos,
  FormElements,
} from 'src/app/interfaces/PWA/Forms';
import { InspectionApiService } from '../InspectionApi/inspection-api.service';
import { SectionStepManagerService } from '../SectionStepManager/section-step-manager.service';
import { AppDB } from 'src/app/config/idb';
import { PhotoDisplay } from 'src/app/interfaces/PWA/Files';
import { PhotoStorageService } from '../../General/PhotoStorage/photo-storage.service';
import { FROM_FIX_ALL_WRONG_PHOTOS } from 'src/app/interfaces/Global/Session';
import { loadEvaluationModule } from 'src/app/pages/inpsection/evaluation/evaluation.loader';

@Injectable({
  providedIn: 'root',
})
export class StepManagerService {
  public currentDynamicStepIdx$ = new BehaviorSubject<number | null>(null);

  constructor(
    private readonly router: Router,
    private readonly inspectionApiService: InspectionApiService,
    private readonly sectionStepManagerService: SectionStepManagerService,
    private readonly db: AppDB,
    private readonly photoStorageService: PhotoStorageService,
  ) {}

  public saveSteps(steps: IInspectionOrderSteps[]) {
    // Primero limpiamos el localStorage en steps
    localStorage.removeItem('steps');
    // TODO: Filtramos el step de daños del flujo actual, ya que no aplica
    localStorage.setItem('steps', JSON.stringify(steps));
  }

  public getSteps(
  ): IInspectionOrderSteps[] {
    const steps = localStorage.getItem('steps');
    if (!steps) {
      throw new Error('Steps not found');
    }
    return JSON.parse(steps);
  }

  public navigateToStep(stepId: EInspectionStep): Promise<boolean> {
    const url = this.getUrlFromStepId(stepId);
    return this.router.navigate([url]);
  }

  /**
   * Carga el chunk de la evaluación sin navegar, por el mismo punto de carga que su ruta: la navegación de
   * después lo encuentra cargado y no depende de la red.
   */
  public preloadEvaluation(): Promise<unknown> {
    return loadEvaluationModule();
  }

  public navigateToStepUrl(url: EInspectionStepUrl) {
    const stepId = this.getStepIdFromUrl(url);
    this.navigateToStep(stepId);
  }

  /**
   * La promesa se resuelve cuando termina la navegación, con guardas y resolvers incluidos.
   * No es `async` a propósito: sin la clave `steps` en localStorage, `getSteps` lanza en sincrónico,
   * y las dos guardas que llaman acá sin esperar recogen ese `throw` en su propia promesa. Con
   * `async` sería un rechazo suelto.
   */
  public navigateToFirstStep(): Promise<boolean> {
    const steps = this.getSteps();
    if (steps.length > 0) {
      const firstStepId = steps[0].id;
      return this.navigateToStep(firstStepId);
    }
    console.error('No steps available to navigate to.');
    return Promise.resolve(false);
  }

  public navigateToFormSection(sectionId: number) {
    this.navigateToStepUrl(EInspectionStepUrl.Forms);
    this.sectionStepManagerService.setCurrentSectionIdx(sectionId);
  }

  public navigateToPhotoId(photoId: number) {
    const steps = this.getSteps();
    const photoStepIndex = steps.findIndex(
      (step) => step.id === EInspectionStep.Photo
    );
    if (photoStepIndex !== -1) {
      localStorage.setItem('photoId', photoId.toString());
      this.router.navigate([EInspectionStepUrl.Photo]).then(() => {
        window.location.reload();
      });
    }
  }

  public async navigateToFirstWrongPhoto() {
    const nextPhotoSectionId = await this.photoStorageService.getPhotoSectionWithWrongAnalysis();
    if (nextPhotoSectionId) {
      this.navigateToPhotoId(nextPhotoSectionId);
      localStorage.setItem(FROM_FIX_ALL_WRONG_PHOTOS, 'true');
    }
  }

  public moveStep(to: 'next' | 'prev', url: string): Promise<boolean> {
    const currentStepIdx = this.getIdxFromUrl(url as EInspectionStepUrl);

    const nextStepIndex =
      to === 'next' ? currentStepIdx + 1 : currentStepIdx - 1;
    if (nextStepIndex < 0) {
      return Promise.resolve(false);
    }
    const stepId = this.getId(nextStepIndex);
    return this.navigateToStep(stepId);
  }

  public getId(idx: number): EInspectionStep {
    const stepOrder = this.getSteps();
    return stepOrder[idx].id;
  }

  public getIdxFromUrl(url: string): number {
    const steps = this.getSteps();
    const stepId = this.getStepIdFromUrl(url);
    return steps.findIndex((step) => step.id === stepId);
  }

  public getUrlFromStepId(stepId: EInspectionStep): EInspectionStepUrl {
    const stepMap: Record<EInspectionStep, EInspectionStepUrl> = {
      [EInspectionStep.Feature]: EInspectionStepUrl.Feature,
      [EInspectionStep.Accesory]: EInspectionStepUrl.Accesory,
      [EInspectionStep.Forms]: EInspectionStepUrl.Forms,
      [EInspectionStep.Photo]: EInspectionStepUrl.Photo,
      [EInspectionStep.Finish]: EInspectionStepUrl.Finish,
      [EInspectionStep.Evaluation]: EInspectionStepUrl.Evaluation,
    };
    return stepMap[stepId];
  }

  public getStepIdFromUrl(url: string): EInspectionStep {
    const stepMap: Record<string, EInspectionStep> = {
      [EInspectionStepUrl.Feature]: EInspectionStep.Feature,
      [EInspectionStepUrl.Accesory]: EInspectionStep.Accesory,
      [EInspectionStepUrl.Forms]: EInspectionStep.Forms,
      [EInspectionStepUrl.Photo]: EInspectionStep.Photo,
      [EInspectionStepUrl.Finish]: EInspectionStep.Finish,
      [EInspectionStepUrl.Evaluation]: EInspectionStep.Evaluation,
    };
    return stepMap[url];
  }


  public async getStepLength(
    screenData: any,
    stepId: EInspectionStep
  ): Promise<number> {
    if (stepId === EInspectionStep.Feature) {
      return (screenData as IFeature).form_elements.length;
    } else if (stepId === EInspectionStep.Accesory) {
      return (screenData as IAccesory).form_elements.length;
    } else if (stepId === EInspectionStep.Photo) {
      return parseInt(localStorage.getItem('total_photos') ?? '0', 10);
    } else if (stepId === EInspectionStep.Forms) {
      const dynamicSections = this.sectionStepManagerService.getSectionSteps();
      return dynamicSections.length;
    } else if (stepId === EInspectionStep.Finish) {
      return 1;
    } else if (stepId === EInspectionStep.Evaluation) {
      return 1;
    }
    return 0;
  }

  public async getFormAnswers(): Promise<FormSectionDataAndPhotos[]> {
    const formSections = await this.db.getAllFormSections();
    return await Promise.all(
      formSections.map(async (section) => {
        const formElements: FormElements = {};
        for (const [elementId, response] of Object.entries(
          section.formElements
        )) {
          const numericElementId = Number(elementId);
          const formResponseElementPhoto = await this.db.getFormElementPhoto(
            numericElementId
          );
          formElements[numericElementId] = {
            label: response.label,
            value: response.value,
            isPhoto: response.isPhoto,
            photos: formResponseElementPhoto?.photos ?? [],
          };
        }
        return {
          id: section.id,
          formId: section.formId,
          title: section.sectionName,
          completed: section.completed,
          formElements,
        } as FormSectionDataAndPhotos;
      })
    );
  }

  public async getPhotoAnswers(): Promise<PhotoDisplay[]> {
    const photoSteps = await this.db.getAllFiles();
    return await Promise.all(
      photoSteps.map(async (photoStep) => {
        return {
          title: photoStep.photoName,
          photoId: photoStep.photoId,
          files: photoStep.files,
        };
      })
    );
  }
}
